import { RadarRepositoryPort } from '../../application/ports/RadarRepositoryPort';
import { LocalStorageRadarRepository } from './LocalStorageRadarRepository';
import { createPersistenceAdapter } from './adapters/RedisAdapter';
import { getEnvironment } from '../config/environment';
import { ApiRadarRepository } from './ApiRadarRepository';

// We'll use more generic types to avoid compatibility issues
/**
 * Factory for creating the appropriate repository implementation
 * based on the current environment.
 * This follows hexagonal architecture by abstracting the storage implementation.
 */
export const createRadarRepository = async (): Promise<RadarRepositoryPort> => {
  try {
    console.log('[RepositoryFactory] Initializing repository');
    const env = getEnvironment();
    console.log('[RepositoryFactory] Environment detected:', {
      isBrowser: env.isBrowser,
      storageType: env.storageType,
      isProduction: env.isProduction,
    });

    const testEnv =
      typeof process !== 'undefined' ? process.env.TEST_ENV : null;

    // Create environment-aware persistence adapter for Redis/LocalStorage
    const persistenceAdapter = createPersistenceAdapter();
    console.log('[RepositoryFactory] Persistence adapter created');

    // For production, integration tests, or podman tests, use DynamoDB
    if (env.isProduction || testEnv === 'integration' || env.isPodmanTest) {
      try {
        console.log(
          '[RepositoryFactory] Initializing DynamoDB for server environment (production/integration/podman)'
        );

        // Dynamically import AWS SDK and DynamoDB repository to prevent bundling
        // Using dynamic import() directly with type casting to avoid typing issues
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const awsConfig: any = await import(
          /* @vite-ignore */ '../config/awsConfig'
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dynModule: any = await import(
          /* @vite-ignore */ './DynamoDBRadarRepository'
        );
        const DynamoDBRadarRepository = dynModule.DynamoDBRadarRepository;

        // Get DynamoDB client
        const dynamoClient = await awsConfig.getDynamoDBClient();

        // Create and return repository with in-memory cache adapter
        return new DynamoDBRadarRepository(dynamoClient, persistenceAdapter);
      } catch (error) {
        console.warn(
          '[RepositoryFactory] Failed to initialize DynamoDBRadarRepository:',
          error
        );
        // Fallback to LocalStorage if DynamoDB initialization fails
        console.log('[RepositoryFactory] Falling back to LocalStorage');
        return new LocalStorageRadarRepository(persistenceAdapter);
      }
    }

    // For localStorage storage type in non-production, non-test environments,
    // always use LocalStorageRadarRepository
    // This avoids any issues with AWS SDK imports
    if (env.storageType === 'localStorage') {
      console.log(
        '[RepositoryFactory] Using LocalStorageRadarRepository as configured by environment'
      );
      return new LocalStorageRadarRepository(persistenceAdapter);
    }

    // Check for direct DynamoDB connection from browser
    if (env.isBrowser && env.storageType === 'dynamoDB') {
      try {
        console.log(
          '[RepositoryFactory] Attempting direct DynamoDB connection from browser'
        );

        // Safely import DynamoDB client
        let DynamoDBClient;
        try {
          // Wrap in try/catch to safely handle import errors
          console.log(
            '[RepositoryFactory] Attempting to import DynamoDB client'
          );
          const awsSdk = await import('@aws-sdk/client-dynamodb');
          DynamoDBClient = awsSdk.DynamoDBClient;
          console.log(
            '[RepositoryFactory] DynamoDB client imported successfully'
          );
        } catch (importError) {
          console.error(
            '[RepositoryFactory] Failed to import DynamoDB client:',
            importError
          );
          console.log('[RepositoryFactory] Falling back to LocalStorage');
          return new LocalStorageRadarRepository(persistenceAdapter);
        }

        // Dynamically import DynamoDB repository
        const dynamoModule = await import(
          /* @vite-ignore */ './DynamoDBRadarRepository'
        );
        const DynamoDBRadarRepository = dynamoModule.DynamoDBRadarRepository;

        // Create DynamoDB client pointing to the specified endpoint
        const dynamoClient = new DynamoDBClient({
          endpoint: env.dynamoDbEndpoint || 'http://localhost:8000',
          region: 'local',
          credentials: {
            accessKeyId: 'local',
            secretAccessKey: 'local',
          },
        });

        console.log(
          '[RepositoryFactory] Created DynamoDB client with endpoint:',
          env.dynamoDbEndpoint || 'http://localhost:8000'
        );

        // Return the DynamoDB repository instance
        return new DynamoDBRadarRepository(dynamoClient, persistenceAdapter);
      } catch (error) {
        console.warn(
          '[RepositoryFactory] Failed to initialize direct DynamoDB connection:',
          error
        );
        // Fallback to LocalStorage if DynamoDB initialization fails
        console.log('[RepositoryFactory] Falling back to LocalStorage');
        return new LocalStorageRadarRepository(persistenceAdapter);
      }
    }

    // Check for API connection from browser
    if (env.isBrowser && env.storageType === 'api') {
      console.log(
        '[RepositoryFactory] Initializing API repository with endpoint:',
        env.apiEndpoint || '/api'
      );

      // Now we can use ApiRadarRepository
      try {
        // ApiRadarRepository has been updated to implement all required methods
        const apiRepository = new ApiRadarRepository();
        return apiRepository;
      } catch (error) {
        console.warn(
          '[RepositoryFactory] Failed to initialize API repository:',
          error
        );
        console.log('[RepositoryFactory] Falling back to LocalStorage');
        return new LocalStorageRadarRepository(persistenceAdapter);
      }
    }

    // Default browser storage or unit tests
    if (env.isBrowser || testEnv === 'unit') {
      console.log(
        '[RepositoryFactory] Using LocalStorageRadarRepository for browser or unit test'
      );
      return new LocalStorageRadarRepository(persistenceAdapter);
    }

    // Default to LocalStorage for development
    console.log(
      '[RepositoryFactory] Using default LocalStorageRadarRepository'
    );
    return new LocalStorageRadarRepository(persistenceAdapter);
  } catch (error) {
    console.error(
      '[RepositoryFactory] Critical error creating repository:',
      error
    );
    // Ultimate fallback - return a basic repository that gracefully handles all errors
    return new LocalStorageRadarRepository();
  }
};
